import { ReactNode } from 'react';

type Primitive = string | number | boolean;

export enum PageLayoutWidth {
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
  XLarge = 'xlarge',
}

export interface PageLayoutProps {
  /*
   * Aria-label for the button to close the right sidebar pane - used in narrow breakpoints
   */
  readonly closeRightSidebarAriaLabel?: string;

  /*
   * Optional prop to collapse left sidebar regardless of screen size.
   */
  readonly collapseLeftSidebar?: boolean;

  /*
   * Allows body not to be scrollable.
   */
  readonly noScroll?: boolean;

  /*
   * Allows content column to occupy more of the available space.
   */
  readonly noSpacing?: boolean;

  /**
   * Optional prop to removes the padding for the scrollbar gutter - used when a scrollbar will not take up space in the margin.
   */
  readonly supressNoScrollRightPadding?: boolean;

  /**
   * Optional prop used to reset scroll and focus change. Reset triggered when this changes.
   */
  readonly resetState?: Primitive;

  /*
   * Aria-label for the button to show the right sidebar pane - used in narrow breakpoints
   */
  readonly showRightSidebarAriaLabel?: string;

  /*
   * Optional prop to force content column to have no top padding.
   */
  readonly suppressTopPadding?: boolean;

  /**
   * Optional prop to apply a full width layout to page.
   */
  readonly fullWidthLayout?: boolean;

  /**
   * Optional render prop for header content. Will render at the top of the page.
   */
  onRenderHeader?(width?: PageLayoutWidth): ReactNode;

  /**
   * Optional render prop for footer content. Will render at the bottom of the page.
   */
  onRenderFooter?(): ReactNode;

  /**
   * Optional render prop for global banner content. Will render below header.
   */
  onRenderBanner?(width?: PageLayoutWidth): ReactNode;

  /**
   * Optional render prop for header pivot. Will render at the top when screen width is small.
   */
  onRenderSubHeader?(): ReactNode;

  /**
   * Optional render prop for near sidebar content. Will render on the left side for ltr, and right for rtl.
   */
  onRenderLeftSidebar?(): ReactNode;
}

export interface PageLayoutContentProps {
  /*
   * Optional prop to collapse right sidebar regardless of screen size.
   */
  readonly collapseRightSidebar?: boolean;

  /*
   * Optional prop to enable sticky scrolling on right sidebar.
   */
  readonly stickyScrollRightSidebar?: boolean;

  /**
   * Optional render prop for far sidebar content. Will render on the right side for ltr, and left for rtl.
   */
  onRenderRightSidebar?(): ReactNode;

  /**
   * Optional render prop for top bar content.
   */
  onRenderTopBar?(): ReactNode;

  /*
   * Optional prop to use wide side bar
   */
  readonly useWiderSidebar?: boolean;
}
